// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import type { PubmedPaperSummary } from '../../../../../services/pubmedService';
import type { AbstractTriageBrief } from '../../../lib/models';
import { formatJournalNameAPA } from '../utils/formatJournalNameAPA';
import { INPUT_SOURCE_LABELS } from '../constants/triageConstants';
import { 
  determinePaperStatus, 
  getStatusDisplay, 
  getStatusColorClass 
} from '../types/paperStatus';


const MAX_TOOLTIP_LENGTH = 160;

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const trimmed = text.slice(0, Math.max(0, maxLength - 3)).trimEnd();
  return `${trimmed}...`;
}

export interface PaperListItemProps {
  paper: PubmedPaperSummary;
  onClick: () => void;
  triageBrief?: AbstractTriageBrief;
  triageError?: { code: string; message: string };
  isLoading?: boolean;
  highlightInputSource?: string | null;
  // NEW: Selection props
  isSelected?: boolean;
  onSelectionChange?: (pmid: string, selected: boolean) => void;
  selectionMode?: boolean; // Whether selection is enabled
  // NEW: Expansion props
  isExpanded?: boolean;
  onExpansionChange?: (pmid: string, expanded: boolean) => void;
  // NEW: AI suggestion props
  aiSuggestion?: 'relevant' | 'not_relevant' | 'failed' | 'not_analyzed';
  confidence?: 'high' | 'medium' | 'low';
}

export const PaperListItem: React.FC<PaperListItemProps> = ({ 
  paper, 
  onClick,
  triageBrief,
  triageError,
  isLoading = false,
  highlightInputSource,
  // NEW: Selection props
  isSelected = false,
  onSelectionChange,
  selectionMode = false,
  // NEW: Expansion props
  isExpanded = false,
  onExpansionChange,
  // NEW: AI suggestion props
  aiSuggestion,
  confidence
}) => {
  // Determine current analysis status
  const analysisStatus = determinePaperStatus(triageBrief, triageError, isLoading);
  const statusDisplay = getStatusDisplay(analysisStatus, triageBrief, triageError);
  const statusColorClass = getStatusColorClass(statusDisplay.color);

  // Derive AI suggestion from triageBrief if not explicitly provided
  const derivedAiSuggestion = aiSuggestion || (
    triageBrief 
      ? (triageBrief.potentialInputSources?.includes('none') ? 'not_relevant' : 'relevant')
      : (triageError ? 'failed' : 'not_analyzed')
  );

  // Click target isolation handlers
  const handleSelectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation(); // Prevent triggering onClick
    onSelectionChange?.(paper.pmid, e.target.checked);
  };

  const handleSelectionClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const handleSelectionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  // Row body click toggles expansion (but not if clicking a link or title)
  const handleRowClick = (e: React.MouseEvent) => {
    // Check if click target is a link or title
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' || target.closest('a')) {
      return; // Don't toggle expansion for links
    }
    if (target.closest('.evidence-drawer__paper-title')) {
      return; // Don't toggle expansion for title clicks (handled separately)
    }
    e.stopPropagation();
    onExpansionChange?.(paper.pmid, !isExpanded);
  };

  // Title click navigates to details view
  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(); // Navigate to DetailsView
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (selectionMode && e.key === ' ') {
        // Space bar toggles selection in selection mode
        onSelectionChange?.(paper.pmid, !isSelected);
      } else {
        // Enter toggles expansion
        onExpansionChange?.(paper.pmid, !isExpanded);
      }
    }
  };

  return (
    <div 
      className={`evidence-drawer__paper-item ${analysisStatus === 'analyzed_relevant' || analysisStatus === 'analyzed_empty' ? 'evidence-drawer__paper-item--triaged' : ''} ${highlightInputSource ? 'evidence-drawer__paper-item--highlighted' : ''} ${isSelected ? 'evidence-drawer__paper-item--selected' : ''} ${isExpanded ? 'evidence-drawer__paper-item--expanded' : ''}`} 
      onClick={handleRowClick} 
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={`Paper: ${paper.title}. ${paper.year ? `Year: ${paper.year}. ` : ''}${paper.journal ? `Journal: ${paper.journal}. ` : ''}${derivedAiSuggestion && derivedAiSuggestion !== 'not_analyzed' ? `AI suggestion: ${derivedAiSuggestion}. ` : ''}Click title to view full details. Click row to ${isExpanded ? 'collapse' : 'expand'} abstract.`}
      aria-expanded={isExpanded}
    >
      {selectionMode && (
        <label
          className="evidence-drawer__paper-selection"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="evidence-drawer__paper-checkbox"
            checked={isSelected}
            onChange={handleSelectionChange}
            onClick={handleSelectionClick}
            onKeyDown={handleSelectionKeyDown}
            aria-label={`Select paper: ${paper.title}`}
          />
        </label>
      )}

      <div className="evidence-drawer__paper-content">
        <div className="evidence-drawer__paper-title">
          <span
            className="evidence-drawer__paper-title-text"
            onClick={handleTitleClick}
            style={{ cursor: 'pointer', flex: 1 }}
          >
            {paper.title}
          </span>
          {/* Enhanced status badge with distinct visual indicators */}
          {analysisStatus !== 'not_analyzed' && (
            <span className={`evidence-drawer__paper-status-badge ${statusColorClass}`} title={statusDisplay.message}>
              {statusDisplay.badge}
            </span>
          )}
        </div>
        
        <div className="evidence-drawer__paper-meta">
          {paper.year && <span>{paper.year}</span>}
          {paper.journal && <span>• {formatJournalNameAPA(paper.journal)}</span>}
          {paper.firstAuthor && <span>• {paper.firstAuthor}</span>}
        </div>

        {/* Status-specific content display */}
        {analysisStatus === 'analyzing' && (
          <div className="evidence-drawer__paper-skeleton" aria-hidden="true" />
        )}

        {(analysisStatus === 'analysis_failed' || analysisStatus === 'analysis_error') && (
          <div className="evidence-drawer__paper-error">
            <span className="evidence-drawer__paper-error-icon">{statusDisplay.icon}</span>
            <span className="evidence-drawer__paper-error-message">{statusDisplay.message}</span>
          </div>
        )}


        {(analysisStatus === 'analyzed_relevant' || analysisStatus === 'analyzed_empty') && triageBrief && (
          <>
            <div className="evidence-drawer__paper-ai-summary">
              {triageBrief.summary}
            </div>

            {/* Combined metadata chips and tags container - only show for relevant papers */}
            {analysisStatus === 'analyzed_relevant' && (
              <div className="evidence-drawer__paper-metadata-row">
                {/* NEW: Retrievable inputs (v2) */}
                {triageBrief.retrievable_inputs && triageBrief.retrievable_inputs.length > 0 && (
                  <div className="evidence-drawer__paper-retrievable-chips">
                    {triageBrief.retrievable_inputs
                      .filter(r => ["explicit_in_abstract", "likely_in_full_text"].includes(r.likelihood))
                      .slice(0, 6)
                      .map(input => (
                        <span
                          key={input.input_key}
                          className="evidence-drawer__paper-retrievable-chip"
                          title={`${input.basis}: ${truncateText(input.supporting_text || "", MAX_TOOLTIP_LENGTH)}`}
                        >
                          {input.input_key}
                        </span>
                      ))
                    }
                    {triageBrief.finland_applicability_overall && (
                      <span className={`evidence-drawer__paper-finland-badge evidence-drawer__paper-finland-badge--${triageBrief.finland_applicability_overall}`}>
                        FI: {triageBrief.finland_applicability_overall}
                      </span>
                    )}
                  </div>
                )}

                {triageBrief.potentialInputSources && triageBrief.potentialInputSources.length > 0 && (
                  <div className="evidence-drawer__paper-tags">
                    {triageBrief.potentialInputSources.map(source => {
                      const isHighlighted = highlightInputSource === source;
                      // Only show highlighted source if one is selected, or show top 2 normally
                      if (highlightInputSource && !isHighlighted) return null;

                      return (
                        <span key={source} className={`evidence-drawer__paper-tag ${isHighlighted ? 'is-highlighted' : ''}`}>
                          {INPUT_SOURCE_LABELS[source] || source}
                        </span>
                      );
                    })}
                    {!highlightInputSource && triageBrief.potentialInputSources.length > 2 && (
                      <span className="evidence-drawer__paper-tag evidence-drawer__paper-tag--more">
                        +{triageBrief.potentialInputSources.length - 2} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Expanded content area - shows full abstract */}
        {isExpanded && (
          <div className="evidence-drawer__paper-expanded-content">
            <div className="evidence-drawer__paper-expanded-content--scrollable">
              {paper.abstract && (
                <>
                  <h4 className="evidence-drawer__abstract-label">Abstract</h4>
                  <p className="evidence-drawer__details-abstract">
                    {paper.abstract}
                  </p>
                </>
              )}
              <div className="evidence-drawer__paper-expanded-links">
                <button
                  className="evidence-drawer__paper-expanded-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                  }}
                >
                  View Full Abstract
                </button>
                <a
                  href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="evidence-drawer__paper-expanded-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Open paper
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Expansion indicator - positioned outside content area */}
      <div className="evidence-drawer__paper-expand-indicator" aria-hidden="true">
        {isExpanded ? '▼' : '▶'}
      </div>
    </div>
  );
};
